import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './InlineSelect.module.scss';

export type InlineSelectValue = string | number;

export interface InlineSelectOption<T extends InlineSelectValue = InlineSelectValue> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
  title?: string;
  dataTestId?: string;
}

interface InlineSelectProps<T extends InlineSelectValue = InlineSelectValue> {
  value: T;
  options: InlineSelectOption<T>[];
  onChange: (value: T) => void;
  id: string;
  ariaLabel: string;
  dataTestId: string;
  className?: string;
}

const InlineSelect = <T extends InlineSelectValue>({
  value,
  options,
  onChange,
  id,
  ariaLabel,
  dataTestId,
  className,
}: InlineSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [open]);

  const listboxId = `${id}-listbox`;
  const triggerLocatorProps = {
    'content-desc': ariaLabel,
  } as React.ButtonHTMLAttributes<HTMLButtonElement> & { 'content-desc'?: string };

  return (
    <div ref={rootRef} className={clsx(styles.root, className)}>
      <button
        type="button"
        id={id}
        className={styles.trigger}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        data-testid={dataTestId}
        onClick={() => setOpen((current) => !current)}
        {...triggerLocatorProps}
      >
        <span className={styles.selectedValue}>{selectedOption?.label ?? String(value)}</span>
        <span className={styles.chevron} aria-hidden="true">⌄</span>
      </button>

      {open ? (
        <ul
          id={listboxId}
          className={styles.listbox}
          role="listbox"
          aria-label={ariaLabel}
          data-testid={`${dataTestId}-listbox`}
        >
          {options.map((option) => {
            const optionLabel = option.ariaLabel || String(option.value);
            const optionLocatorProps = {
              'content-desc': optionLabel,
            } as React.ButtonHTMLAttributes<HTMLButtonElement> & { 'content-desc'?: string };

            return (
              <li
                key={String(option.value)}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                className={clsx(styles.optionRow, {
                  [styles.selected]: option.value === value,
                  [styles.disabled]: option.disabled,
                })}
              >
                <button
                  type="button"
                  className={styles.optionButton}
                  disabled={option.disabled}
                  aria-label={optionLabel}
                  title={option.title}
                  data-testid={option.dataTestId}
                  onClick={() => {
                    if (option.disabled) return;
                    onChange(option.value);
                    setOpen(false);
                  }}
                  {...optionLocatorProps}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default InlineSelect;
